import React from "react";
import ServiceHeroSection from "../common/service-hero-section";
import { AiBlog } from "@/components/main";
import {
  FeatureGrid,
  ProcessSteps,
  RelatedServices,
  ScenarioGrid,
  ServiceAudience,
  ServiceBottomCta,
  ServiceFaq,
  ServiceSection,
  ServiceSectionHeader,
  SplitContent,
} from "../common/service-content-blocks";

const CAPABILITIES = [
  {
    title: "ERP and CRM connectors",
    description:
      "Read and write the records your teams already trust, with mapping and error handling designed for production traffic.",
  },
  {
    title: "Internal API bridges",
    description:
      "Connect agents and LLM apps to services that were never built for AI, without fragile screen scraping as the default.",
  },
  {
    title: "Event and webhook flows",
    description:
      "Trigger AI workflows from system events and push outcomes back with reliable delivery semantics.",
  },
  {
    title: "Identity and permissions",
    description:
      "Respect SSO, roles, and data boundaries so AI actions stay within approved access.",
  },
  {
    title: "Data sync and enrichment",
    description:
      "Keep context fresh across tools so agents and assistants are not working on stale state.",
  },
  {
    title: "Observability hooks",
    description:
      "Logs, retries, and alerts so integration failures are visible and recoverable.",
  },
];

const PATTERNS = [
  {
    title: "System of record writes",
    description:
      "AI proposes or applies updates through official APIs with validation and audit.",
  },
  {
    title: "Context assembly",
    description:
      "Pull the right fields from multiple systems before an agent or assistant acts.",
  },
  {
    title: "Human approval gates",
    description:
      "Hold high-impact writes until an owner confirms, then continue the flow.",
  },
  {
    title: "Idempotent retries",
    description:
      "Design for partial failure so retries do not duplicate side effects.",
  },
];

const SCENARIOS = [
  {
    title: "CRM-assisted support",
    description:
      "Assistants and agents read ticket and account context, then update cases safely.",
    outcome: "Less copy-paste between tools.",
  },
  {
    title: "ERP document posting",
    description:
      "Extracted document data posts into ERP with validation against master data.",
    outcome: "Cleaner posting with fewer rejects.",
  },
  {
    title: "Warehouse and WMS signals",
    description:
      "Operational events trigger AI workflows that notify and update status systems.",
    outcome: "Faster reaction to exceptions.",
  },
  {
    title: "Internal knowledge + identity",
    description:
      "Knowledge assistants only retrieve documents the user is allowed to see.",
    outcome: "Useful answers without permission leaks.",
  },
];

const DELIVERY = [
  {
    title: "Inventory systems",
    description: "Map APIs, auth, rate limits, and owners.",
  },
  {
    title: "Design contracts",
    description: "Define payloads, failure modes, and review gates.",
  },
  {
    title: "Build and harden",
    description: "Implement with retries, logging, and staging validation.",
  },
  {
    title: "Operate",
    description: "Hand off runbooks and monitoring to your platform team.",
  },
];

export const FAQS = [
  {
    question: "Do you replace our systems?",
    answer:
      "No. Integrations are designed to extend the stack you already run.",
  },
  {
    question: "What if APIs are incomplete?",
    answer:
      "We assess options honestly: API work, middleware, or a narrower scope. We avoid brittle approaches unless you explicitly accept the trade-off.",
  },
  {
    question: "Who maintains connectors?",
    answer:
      "We document ownership. Many clients keep platform engineering in the loop for day-two changes.",
  },
];

function AiIntegrationsComponent({ posts = [] }) {
  return (
    <>
      <ServiceHeroSection
        headlinePrimary="Connect AI to"
        headlineHighWhite="the systems you already trust"
        descriptionPrimary="We integrate agents, LLM applications, and automation into ERP, CRM, and internal platforms without forcing a rip-and-replace."
        descriptionSecondary="Reliable connectors, clear contracts, and observability built for production."
      />

      <ServiceAudience
        forItems={[
          "You have systems of record that must stay authoritative",
          "AI needs to read or write operational data safely",
          "Identity and permissions are non-negotiable",
          "You can involve system owners in design",
        ]}
        notForItems={[
          "You want AI to replace the ERP or CRM",
          "There is no API or integration path and no appetite to create one",
          "Permission models are undefined",
          "You need a demo-only connection with no production plan",
        ]}
      />

      <ServiceSection>
        <ServiceSectionHeader
          title="Integration work we take on"
          lead="The connective tissue that makes AI useful inside real operations."
        />
        <FeatureGrid items={CAPABILITIES} />
      </ServiceSection>

      <ServiceSection muted>
        <SplitContent
          title="Patterns that stay reliable"
          lead="We prefer boring, operable patterns over clever one-offs."
          points={PATTERNS}
        />
      </ServiceSection>

      <ServiceSection>
        <ServiceSectionHeader
          title="Where integrations unlock AI"
        />
        <ScenarioGrid items={SCENARIOS} />
      </ServiceSection>

      <ServiceSection muted>
        <ServiceSectionHeader title="How we deliver" />
        <ProcessSteps steps={DELIVERY} />
      </ServiceSection>

      <ServiceFaq items={FAQS} />
      <ServiceBottomCta
        title="Need AI connected to your stack?"
        description="Share the systems, auth model, and workflows involved. We will map a safe integration path."
      />
      <RelatedServices
        items={[
          { href: "/ai-agent-development", label: "AI agents" },
          { href: "/custom-llm-application-development", label: "LLM applications" },
          { href: "/ai-workflow-automation", label: "Workflow automation" },
          { href: "/mvp-poc-development", label: "MVP and PoC" },
        ]}
      />
      <AiBlog posts={posts} />
    </>
  );
}

export default AiIntegrationsComponent;
